import React, { useCallback, useEffect } from "react";
import { T, useT } from "@transifex/react";
import toast from "storybook/components/toast";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { usePatientAuth } from "context/patient-auth";
import { printPdf } from "helpers/download-pdf";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  SectionLabel,
  MethodList,
  MethodCard,
  MethodIcon,
  MethodLabel,
  ModalFooter,
  CancelButton,
} from "./styles";

export interface SendResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportPdf: string | null;
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
      fill="currentColor"
    />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6l-10 7L2 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PrintIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18 14H6v8h12v-8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function SendResultsModal({
  isOpen,
  onClose,
  reportPdf,
}: SendResultsModalProps) {
  const t = useT();
  const { logout } = usePatientAuth();

  const handleEmailToSelf = useCallback(async () => {
    onClose();
    try {
      const patientId = localStorage.getItem("ciq_id");
      const idToken = localStorage.getItem("id_token");
      const response = await fetch(
        `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/risk_reports/current/email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      if (response.ok) {
        toast.success(t("Results sent to your email"));
      } else if (response.status === 401) {
        logout();
      } else {
        toast.error(t("Unable to send email. Please try again."));
      }
    } catch {
      toast.error(t("Unable to send email. Please try again."));
    }
  }, [onClose, t, logout]);

  const handlePrint = useCallback(() => {
    onClose();
    if (!printPdf(reportPdf)) {
      toast.error(t("No report available to print"));
    }
  }, [onClose, reportPdf, t]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-results-title"
      data-testid="SendResultsModal"
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle id="send-results-title">
            <T _str="Send Your Results" />
          </ModalTitle>
          <ModalSubtitle>
            <T _str="Choose how you'd like to receive your risk assessment results." />
          </ModalSubtitle>
        </ModalHeader>

        <CloseButton
          onClick={onClose}
          aria-label={t("Close")}
          data-testid="SendResultsModal__CloseButton"
        >
          <CloseIcon />
        </CloseButton>

        <SectionLabel>
          <T _str="Select Delivery Method" />
        </SectionLabel>

        <MethodList>
          <MethodCard
            onClick={handleEmailToSelf}
            data-testid="SendResultsModal__EmailToSelf"
          >
            <MethodIcon>
              <EmailIcon />
            </MethodIcon>
            <MethodLabel>
              <T _str="Email to Myself" />
            </MethodLabel>
          </MethodCard>

          <MethodCard
            onClick={handlePrint}
            data-testid="SendResultsModal__Print"
          >
            <MethodIcon>
              <PrintIcon />
            </MethodIcon>
            <MethodLabel>
              <T _str="Print" />
            </MethodLabel>
          </MethodCard>
        </MethodList>

        <ModalFooter>
          <CancelButton
            onClick={onClose}
            data-testid="SendResultsModal__CancelButton"
          >
            <T _str="Cancel" />
          </CancelButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}
